import axios from 'axios'
import Api from 'clients/web/Api'
import WebRepository from 'clients/web/WebRepository'
import Allergy from 'model/Allergy'
import InternalExaminationPlan from 'model/InternalExaminationPlan'
import PatientFile from 'model/PatientFile'
import PatientsDiagnosis from 'model/PatientsDiagnosis'
import PatientsDiagnosisMedications from 'model/PatientsDiagnosisMedications'
import PatientsDiagnosisSimple from 'model/PatientsDiagnosisSimple'
import PatientsDiagnosisTopics from 'model/PatientsDiagnosisTopics'
import PatientDiagnosisDiscussions from '../../model/PatientsDiagnosisDiscussions'

const { server } = (window as any).config

export class PatientDiagnosisRepository extends WebRepository<
  PatientsDiagnosis | PatientsDiagnosisTopics
> {
  constructor() {
    super(new Api('patients_diagnosis'))
  }

  async findAllPatientTopics(patientId: object): Promise<PatientsDiagnosisTopics[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientsTopics', {
      params: patientId,
    })
    return result.data?.patientTopics
  }

  async findAllPatientDiagnosis(patientId: object): Promise<PatientsDiagnosis[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientDiagnosis', {
      params: patientId,
    })
    return result.data?.patientDiagnosis
  }

  async findAllPatientMedications(patientId: object): Promise<PatientsDiagnosisMedications[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientMedications', {
      params: patientId,
    })
    return result.data?.patientMedications
  }

  async findAllPatientAllergies(patientId: object): Promise<Allergy[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientAllergies', {
      params: patientId,
    })
    return result.data?.patientAllergies
  }

  async findAllPatientExaminations(patientId: object): Promise<any[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientExaminations', {
      params: patientId,
    })
    return result.data?.patientExaminations
  }

  async findAllPatientSchedulePlans(patientId: object): Promise<InternalExaminationPlan[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientSchedulePlans', {
      params: patientId,
    })
    return result.data?.schedulePlans
  }

  async findAllPatientDiscussions(patientId: object): Promise<PatientDiagnosisDiscussions[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientDiscussions', {
      params: patientId,
    })
    return result.data?.patientDiscussions
  }

  async findAllPatientMedicalReferralResults(patientId: object): Promise<PatientFile[]> {
    const result = await axios.get(
      server + '/patients_diagnosis/findAllPatientMedicalReferralResults',
      {
        params: patientId,
      },
    )
    return result.data?.patientMedicalReferralResults
  }

  async getAppointmentOwner(patientId: object) {
    const result = await axios.get(server + '/patients_diagnosis/getAppointmentOwner', {
      params: patientId,
    })
    return result.data
  }

  async setAppointmentOwner(patientId: string, userId: string | null) {
    const result = await axios.get(server + '/patients_diagnosis/setAppointmentOwner', {
      params: { patientId, userId },
    })
    return result.data
  }

  async createTopic(
    topic: PatientsDiagnosisTopics,
    firstDiagnosis: PatientsDiagnosis,
  ): Promise<PatientsDiagnosisTopics> {
    const newTopic = await axios.post(server + '/patients_diagnosis/createTopic', {
      topic,
      firstDiagnosis,
    })
    return newTopic.data
  }

  async addDiagnosisToTopic(
    topicId: string,
    diagnosis: PatientsDiagnosis,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/addDiagnosisToTopic', {
      topicId,
      diagnosis,
    })
    return updatedTopic.data
  }

  async mergeTopics(
    topicId: string,
    diagnosis: PatientsDiagnosis,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/mergeTopics', {
      topicId,
      diagnosis,
    })
    return updatedTopic.data
  }

  async deleteTopic(topicId: string): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/deleteTopic', {
      topicId,
    })
    return updatedTopic.data
  }

  async updateTopicStatus(topicId: string, status: string): Promise<PatientsDiagnosisTopics> {
    const updatedStatus = await axios.post(server + '/patients_diagnosis/updateTopicStatus', {
      topicId,
      status,
    })
    return updatedStatus.data
  }

  async addDiagnosis(diagnosis: PatientsDiagnosis): Promise<PatientsDiagnosis> {
    const newDiagnosis = await axios.post(server + '/patients_diagnosis/createNewDiagnosis', {
      diagnosis,
    })
    return newDiagnosis.data
  }

  async updateDiagnosisInTopic(
    topicId: string,
    diagnosisToUpdate: PatientsDiagnosisSimple,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/updateDiagnosisInTopic', {
      topicId,
      diagnosisToUpdate,
    })
    return updatedTopic.data
  }

  async getMedicationHistory(patientId: any, medicationId: any): Promise<any[]> {
    const result = await axios.get(server + '/patients_diagnosis/getMedicationHistory', {
      params: { patientId, medicationId },
    })

    return result.data?.medicationHistory
  }

  async editMedicationHistory(patientId: any, medicationId: any, history: any) {
    await axios.post(server + '/patients_diagnosis/editMedicationHistory', {
      patientId,
      medicationId,
      history,
    })
  }

  async addDiscussionToTopic(
    discussionToAdd: PatientDiagnosisDiscussions,
  ): Promise<PatientDiagnosisDiscussions> {
    const result = await axios.post(server + '/patients_diagnosis/addDiscussionToTopic', {
      discussionToAdd,
    })

    return result.data?.newDiscussion
  }

  async updateDiscussionInTopic(
    discussionToUpdate: PatientDiagnosisDiscussions,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedDiscussion = await axios.post(
      server + '/patients_diagnosis/updateDiscussionInTopic',
      {
        discussionToUpdate,
      },
    )
    return updatedDiscussion.data.updatedDiscussion
  }

  async addMedicationToTopic(medicationToAdd: PatientsDiagnosisMedications) {
    const result = await axios.post(server + '/patients_diagnosis/addMedicationToTopic', {
      medicationToAdd,
    })

    return result.data?.newMedication
  }

  async updateMedication(medicationToUpdate: PatientsDiagnosisMedications) {
    const result = await axios.post(server + '/patients_diagnosis/updateMedication', {
      medicationToUpdate,
    })

    return result.data?.updatedMedication
  }

  async addAllergy(allergyToAdd: Allergy) {
    const result = await axios.post(server + '/patients_diagnosis/addAllergy', {
      allergyToAdd,
    })

    return result.data?.newAllergy
  }

  async updateAllergy(allergyToUpdate: Allergy) {
    const result = await axios.post(server + '/patients_diagnosis/updateAllergy', {
      allergyToUpdate,
    })

    return result.data?.updatedAllergy
  }

  async createInternalExaminationPlan(examPlan: InternalExaminationPlan) {
    const result = await axios.post(server + '/patients_diagnosis/createInternalExaminationPlan', {
      examPlan,
    })

    return result
  }

  async closeInternalExaminationPlan(examPlanId: string, discussion: string) {
    const result = await axios.post(server + '/patients_diagnosis/closeInternalExaminationPlan', {
      examPlanId,
      discussion,
    })

    return result
  }

  async checkIfScheduleExists(examPlan: any) {
    const result = await axios.post(server + '/patients_diagnosis/checkIfScheduleExists', {
      examPlan,
    })

    return result
  }

  async signMonitoringAsViewed(patientId: any) {
    const result = await axios.post(server + '/patients_diagnosis/signMonitoringAsViewed', {
      patientId,
    })

    return result
  }

  async signLabsAsViewed(patientId: any) {
    const result = await axios.post(server + '/patients_diagnosis/signLabsAsViewed', {
      patientId,
    })

    return result
  }

  async sendDiagnosisToSpecialist(patientId: string, speciality: string, diagnosisCode: string) {
    const result = await axios.post(server + '/patients_diagnosis/sendDiagnosisToSpecialist', {
      patientId,
      speciality,
      diagnosisCode,
    })

    return result
  }
}

export default new PatientDiagnosisRepository()
